import NextLink from "next/link";

import type { JSX } from "react";
import type LinkProps from "./types";

/**
 * @name Link
 * @description A link atom component that wraps Next.js Link component.
 * It provides client-side navigation between routes.
 *
 * @param {LinkProps} props - The props for the link component.
 * @param {React.ReactNode} props.children - The content to be rendered inside the link.
 * @param {string} props.href - The URL path to navigate to.
 * @param {string} [props.className] - Optional CSS classes for styling the link.
 * @returns {JSX.Element} The rendered Next.js Link element.
 *
 * @example
 * <Link href="/about" className="text-blue-500 hover:underline">
 *   About Us
 * </Link>
 */
const Link = ({ children, ...props }: LinkProps): JSX.Element => {
  // target="_blank" without rel="noopener" lets the opened page reach back
  // via window.opener and redirect this tab (reverse tabnabbing) — Next's
  // <Link> doesn't add this itself, so every "_blank" link needs it here.
  // Menu items with a backend-controlled `target`/`href` (see Sidebar*)
  // are exactly the case this protects against.
  const rel =
    props.target === "_blank" && !props.rel ? "noopener noreferrer" : props.rel;

  return (
    <NextLink {...props} rel={rel}>
      {children}
    </NextLink>
  );
};

export default Link;
